import { Box, ButtonBase, Paper } from '@mui/material';

import { useWallStore, useWorkspaceUiStore } from '@walls/store';
import { getPanelColor } from '../theme/wallThemeStyles';
import { boardCardColors } from '../../board/card-actions/utils/cardColors';

const cardShapes = [
  { label: 'Card', value: 'card', icon: <CardShapeIcon /> },
  { label: 'Rounded', value: 'rounded', icon: <RoundedCardShapeIcon /> },
  { label: 'Circle', value: 'circle', icon: <CircleCardShapeIcon /> },
  { label: 'Triangle', value: 'triangle', icon: <TriangleCardShapeIcon /> },
];

interface CardToolDialogProps {
  layoutStyle?: string;
  onColorSelect?: () => void;
}

export function CardToolDialog({ layoutStyle, onColorSelect }: CardToolDialogProps) {
  const selectedCardColor = useWorkspaceUiStore((state) => state.selectedCardColor);
  const selectedCardShape = useWorkspaceUiStore((state) => state.selectedCardShape);
  const setSelectedCardColor = useWorkspaceUiStore((state) => state.setSelectedCardColor);
  const setSelectedCardShape = useWorkspaceUiStore((state) => state.setSelectedCardShape);
  const theme = useWallStore((state) => state.theme);
  const panelColor = getPanelColor(theme);

  return (
    <Paper
      elevation={0}
      onClick={(event) => event.stopPropagation()}
      sx={{
        bgcolor: panelColor,
        boxShadow: '0px 3px 6px #00000029',
        bottom: {
          xs: layoutStyle === 'open' ? 'auto' : 10,
          sm: 'auto',
        },
        left: { xs: 68, sm: 78 },
        maxHeight: 'calc(100vh - 20px)',
        overflowY: 'auto',
        position: 'fixed',
        top: {
          xs: layoutStyle === 'open' ? 70 : 'auto',
          sm: '50%',
        },
        transform: { xs: 'none', sm: 'translateY(-50%)' },
        width: { xs: 'min(235px, calc(100vw - 78px))', sm: 235 },
        zIndex: 1001,
      }}
    >
      <Box sx={{ borderBottom: '2px solid #f3f5f8', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)' }}>
        {cardShapes.map((shape) => (
          <ButtonBase
            key={shape.value}
            aria-label={`Card shape ${shape.label}`}
            onClick={() => setSelectedCardShape(shape.value)}
            sx={{
              bgcolor: selectedCardShape === shape.value ? 'rgba(255, 255, 255, 0.25)' : 'transparent',
              height: 62,
              p: 1,
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.25)' },
            }}
          >
            {shape.icon}
          </ButtonBase>
        ))}
      </Box>

      <Box
        role="radiogroup"
        aria-label="Card color"
        sx={{
          display: 'grid',
          gap: 1,
          gridTemplateColumns: 'repeat(4, 1fr)',
          p: 1.25,
        }}
      >
        {boardCardColors.map((color) => {
          const selected = color.toLowerCase() === selectedCardColor.toLowerCase();

          return (
            <Box
              key={color}
              component="button"
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={`Card color ${color}`}
              onClick={() => {
                setSelectedCardColor(color);
                onColorSelect?.();
              }}
              sx={{
                bgcolor: color,
                border: selected ? '2px solid #0f2a39' : '1px solid #d0d5dd',
                borderRadius: '50%',
                boxShadow:
                  color.toLowerCase() === '#ffffff' || color.toLowerCase() === '#f9faf9'
                    ? 'inset 0 0 0 1px #e4e7ec'
                    : selected
                    ? '0 0 0 2px #d8f7ff'
                    : undefined,
                cursor: 'pointer',
                height: 40,
                justifySelf: 'center',
                p: 0,
                width: 40,
              }}
            />
          );
        })}
      </Box>
    </Paper>
  );
}

function CardShapeIcon() {
  return (
    <svg aria-hidden="true" height="42" viewBox="0 0 42 42">
      <rect x="6" y="6" width="30" height="30" fill="#ffffff" stroke="#0f2a39" strokeWidth="2" />
    </svg>
  );
}

function RoundedCardShapeIcon() {
  return (
    <svg aria-hidden="true" height="42" viewBox="0 0 42 42">
      <rect
        x="6"
        y="6"
        width="30"
        height="30"
        rx="7"
        fill="#ffffff"
        stroke="#0f2a39"
        strokeWidth="2"
      />
    </svg>
  );
}

function CircleCardShapeIcon() {
  return (
    <svg aria-hidden="true" height="42" viewBox="0 0 42 42">
      <circle cx="21" cy="21" r="15" fill="#ffffff" stroke="#0f2a39" strokeWidth="2" />
    </svg>
  );
}

function TriangleCardShapeIcon() {
  return (
    <svg aria-hidden="true" height="42" viewBox="0 0 42 42">
      <path d="M21 6 L37 35 H5 Z" fill="#ffffff" stroke="#0f2a39" strokeWidth="2" />
    </svg>
  );
}
